/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Webhooks Hooks
 * ============================================================================
 *
 * React Query hooks for webhook management.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhooksService } from '@/services/superadmin/webhooks.service';
import {
  Webhook,
  WebhookDelivery,
  CreateWebhookRequest,
  UpdateWebhookRequest,
  WebhookDeliveryStatus,
} from '@/types/superadmin.types';

// Query keys
export const webhookKeys = {
  all: ['webhooks'] as const,
  lists: () => [...webhookKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...webhookKeys.lists(), filters] as const,
  details: () => [...webhookKeys.all, 'detail'] as const,
  detail: (id: string) => [...webhookKeys.details(), id] as const,
  deliveries: (id: string) => [...webhookKeys.all, 'deliveries', id] as const,
  stats: () => [...webhookKeys.all, 'stats'] as const,
  events: () => [...webhookKeys.all, 'events'] as const,
};

/**
 * Hook to fetch webhooks list
 */
export function useWebhooks(params?: {
  page?: number;
  size?: number;
  active?: boolean;
  search?: string;
  sort?: string;
}) {
  return useQuery({
    queryKey: webhookKeys.list(params || {}),
    queryFn: () => webhooksService.getWebhooks(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a single webhook
 */
export function useWebhook(id: string | undefined) {
  return useQuery({
    queryKey: webhookKeys.detail(id || ''),
    queryFn: () => webhooksService.getWebhook(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to create a webhook
 */
export function useCreateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWebhookRequest) => webhooksService.createWebhook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.lists() });
      queryClient.invalidateQueries({ queryKey: webhookKeys.stats() });
    },
  });
}

/**
 * Hook to update a webhook
 */
export function useUpdateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWebhookRequest }) =>
      webhooksService.updateWebhook(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: webhookKeys.lists() });
      queryClient.invalidateQueries({ queryKey: webhookKeys.stats() });
    },
  });
}

/**
 * Hook to delete a webhook
 */
export function useDeleteWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => webhooksService.deleteWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.lists() });
      queryClient.invalidateQueries({ queryKey: webhookKeys.stats() });
    },
  });
}

/**
 * Hook to test a webhook
 */
export function useTestWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => webhooksService.testWebhook(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries(id) });
    },
  });
}

/**
 * Hook to regenerate webhook secret
 */
export function useRegenerateSecret() {
  return useMutation({
    mutationFn: (id: string) => webhooksService.regenerateSecret(id),
  });
}

/**
 * Hook to get webhook secret
 */
export function useWebhookSecret(id: string | undefined, enabled: boolean = false) {
  return useQuery({
    queryKey: [...webhookKeys.detail(id || ''), 'secret'],
    queryFn: () => webhooksService.getSecret(id!),
    enabled: !!id && enabled,
    staleTime: 0, // Always refetch
  });
}

/**
 * Hook to reset webhook circuit breaker
 */
export function useResetCircuit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => webhooksService.resetCircuit(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: webhookKeys.lists() });
      queryClient.invalidateQueries({ queryKey: webhookKeys.stats() });
    },
  });
}

/**
 * Hook to fetch webhook deliveries
 */
export function useWebhookDeliveries(
  webhookId: string | undefined,
  params?: {
    page?: number;
    size?: number;
    status?: WebhookDeliveryStatus;
  }
) {
  return useQuery({
    queryKey: [...webhookKeys.deliveries(webhookId || ''), params],
    queryFn: () => webhooksService.getDeliveries(webhookId!, params),
    enabled: !!webhookId,
    staleTime: 10 * 1000, // 10 seconds
  });
}

/**
 * Hook to retry a delivery
 */
export function useRetryDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deliveryId: string) => webhooksService.retryDelivery(deliveryId),
    onSuccess: () => {
      // Invalidate all deliveries queries
      queryClient.invalidateQueries({ queryKey: webhookKeys.all });
    },
  });
}

/**
 * Hook to fetch webhook statistics
 */
export function useWebhookStats() {
  return useQuery({
    queryKey: webhookKeys.stats(),
    queryFn: () => webhooksService.getStats(),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch available events
 */
export function useAvailableEvents() {
  return useQuery({
    queryKey: webhookKeys.events(),
    queryFn: () => webhooksService.getAvailableEvents(),
    staleTime: 5 * 60 * 1000, // 5 minutes (rarely changes)
  });
}
